import React, { useRef, useState, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import * as THREE from 'three';
import { shared } from './shared';
import { useStore } from '../store';

// A stylized 3D Harpy Eagle guide that follows/leads the traveller and glows while narrating.
export default function HarpyEagle() {
  const grp = useRef();
  const leftWing = useRef();
  const rightWing = useRef();
  const crest = useRef();
  const nightGlow = useRef();
  const eyeL = useRef();
  const eyeR = useRef();
  const narrating = useStore((s) => s.narrating);
  const caption = useStore((s) => s.caption);
  const captionLabel = useStore((s) => s.captionLabel);
  const panelsHidden = useStore((s) => s.panelsHidden);
  const audioEnabled = useStore((s) => s.audioEnabled);
  const guideVolume = useStore((s) => s.guideVolume);

  // Floating speech bubble is shown only while the eagle is actively briefing,
  // and is silenced/hidden instantly by Hide Panels, Master Mute, or Mute Guide.
  const showBubble = narrating && !!caption && !panelsHidden && audioEnabled && guideVolume > 0.01;

  // Auto-fit: long briefings collapse to ~2 lines with a "Read more" tap.
  const [expanded, setExpanded] = useState(false);
  const LONG = 130; // chars beyond which the caption is considered long
  const isLong = !!caption && caption.length > LONG;
  useEffect(() => { setExpanded(false); }, [caption]);

  const pos = useRef(new THREE.Vector3(0, 20, 40));
  const tmpLook = useRef(new THREE.Vector3());

  useFrame((state, delta) => {
    if (!grp.current) return;
    const t = state.clock.elapsedTime;
    const night = shared.env.night;

    // Ease toward the shared target with a gentle hover offset.
    const target = shared.eagleTarget;
    pos.current.lerp(target, Math.min(1, delta * 1.6));
    grp.current.position.copy(pos.current);
    grp.current.position.y += Math.sin(t * 1.5) * 0.6;
    shared.eaglePos.copy(grp.current.position);

    // Face travel direction (toward the look target).
    tmpLook.current.copy(shared.lookTarget);
    grp.current.lookAt(tmpLook.current);

    // Wing flap
    const flap = Math.sin(t * 6) * 0.5;
    if (leftWing.current) leftWing.current.rotation.z = 0.2 + flap;
    if (rightWing.current) rightWing.current.rotation.z = -0.2 - flap;
    if (crest.current) crest.current.intensity = narrating ? 2.4 + Math.sin(t * 8) * 0.6 : 0.5;
    // Soft white bioluminescence at night so the guide stays visible in the dark canopy
    if (nightGlow.current) nightGlow.current.intensity = night * 1.6 + 0.15;
    const eyeI = narrating ? 2.6 + Math.sin(t * 10) * 0.8 : 1.0 + night * 0.6;
    if (eyeL.current) eyeL.current.material.emissiveIntensity = eyeI;
    if (eyeR.current) eyeR.current.material.emissiveIntensity = eyeI;
  });

  const feather = <meshStandardMaterial color="#eef0f2" emissive="#ffffff" emissiveIntensity={0.06} roughness={0.55} flatShading />;
  const dark = <meshStandardMaterial color="#3a3d44" roughness={0.7} flatShading />;

  return (
    <group ref={grp} position={[0, 20, 40]} scale={1.6}>
      {/* Floating 3D speech bubble that follows the eagle while it narrates */}
      {showBubble && (
        <Html
          data-testid="eagle-speech-bubble"
          center
          distanceFactor={22}
          position={[0, 2.6, 0]}
          zIndexRange={[100, 0]}
          style={{ pointerEvents: 'none' }}
        >
          <div
            style={{
              position: 'relative', maxWidth: 300, padding: '12px 16px', borderRadius: 16,
              background: 'rgba(8,11,17,0.9)', border: '1px solid rgba(212,175,55,0.55)',
              boxShadow: '0 10px 34px rgba(0,0,0,0.55)', textAlign: 'center', backdropFilter: 'blur(8px)',
            }}
          >
            {captionLabel && (
              <div style={{ color: '#D4AF37', fontFamily: 'monospace', fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', marginBottom: 6 }}>
                {captionLabel}
              </div>
            )}
            <div
              style={{
                color: '#ffffff', fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 15, lineHeight: 1.45,
                ...(isLong && !expanded
                  ? { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }
                  : {}),
              }}
            >
              {caption}
            </div>
            {isLong && (
              <button
                data-testid="eagle-bubble-readmore"
                onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
                style={{
                  pointerEvents: 'auto', cursor: 'pointer', marginTop: 8, padding: '3px 12px', borderRadius: 9999,
                  background: 'rgba(212,175,55,0.16)', border: '1px solid rgba(212,175,55,0.5)', color: '#D4AF37',
                  fontFamily: 'monospace', fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase',
                }}
              >
                {expanded ? 'Show less' : 'Read more'}
              </button>
            )}
            <div style={{ position: 'absolute', bottom: -8, left: '50%', width: 16, height: 16, transform: 'translateX(-50%) rotate(45deg)', background: 'rgba(8,11,17,0.9)', borderRight: '1px solid rgba(212,175,55,0.55)', borderBottom: '1px solid rgba(212,175,55,0.55)' }} />
          </div>
        </Html>
      )}
      <pointLight ref={crest} color="#D4AF37" distance={16} intensity={0.6} position={[0, 1, 0]} />
      <pointLight ref={nightGlow} color="#eaf2ff" distance={22} intensity={0.15} position={[0, 0.5, 0]} />
      {/* Body */}
      <mesh castShadow>
        <capsuleGeometry args={[0.5, 1.1, 4, 8]} />
        {feather}
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.35, 0.95]} castShadow>
        <sphereGeometry args={[0.42, 10, 8]} />
        <meshStandardMaterial color="#f2ede1" roughness={0.5} flatShading />
      </mesh>
      {/* Crest feathers */}
      {[-0.18, 0, 0.18].map((x, i) => (
        <mesh key={i} position={[x, 0.75, 0.9]} rotation={[0.3, 0, x * 2]}>
          <coneGeometry args={[0.08, 0.4, 4]} />
          {dark}
        </mesh>
      ))}
      {/* Beak */}
      <mesh position={[0, 0.28, 1.35]} rotation={[Math.PI / 2, 0, 0]}>
        <coneGeometry args={[0.12, 0.35, 6]} />
        <meshStandardMaterial color="#1a1a1c" flatShading />
      </mesh>
      {/* Eyes */}
      {[-0.16, 0.16].map((x, i) => (
        <mesh key={i} ref={i === 0 ? eyeL : eyeR} position={[x, 0.42, 1.15]}>
          <sphereGeometry args={[0.07, 6, 6]} />
          <meshStandardMaterial color="#D4AF37" emissive="#D4AF37" emissiveIntensity={1.2} toneMapped={false} />
        </mesh>
      ))}
      {/* Wings */}
      <group ref={leftWing} position={[-0.4, 0.1, 0]}>
        <mesh position={[-1.1, 0, 0]} castShadow>
          <boxGeometry args={[2.4, 0.08, 1.1]} />
          {feather}
        </mesh>
        <mesh position={[-2.3, 0, -0.2]} rotation={[0, 0, 0.1]}>
          <boxGeometry args={[1.2, 0.06, 0.7]} />
          {dark}
        </mesh>
      </group>
      <group ref={rightWing} position={[0.4, 0.1, 0]}>
        <mesh position={[1.1, 0, 0]} castShadow>
          <boxGeometry args={[2.4, 0.08, 1.1]} />
          {feather}
        </mesh>
        <mesh position={[2.3, 0, -0.2]} rotation={[0, 0, -0.1]}>
          <boxGeometry args={[1.2, 0.06, 0.7]} />
          {dark}
        </mesh>
      </group>
      {/* Tail */}
      <mesh position={[0, -0.1, -1.1]} rotation={[0.2, 0, 0]}>
        <boxGeometry args={[0.9, 0.06, 1.0]} />
        {dark}
      </mesh>
    </group>
  );
}
